import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { TextAreaField } from "@/components/ui/FormFields";
import * as events from "@/api/events";
import type { EventSummary } from "@/api/events";

// ═══════════════════════════════════════════════════════════════════
// The organiser's desk for one event.
//
// Everything here is behind an ownership check, so on somebody else's event
// every panel should refuse. That is the most useful thing this page does:
// the refusals ARE the surface, and they are shown in the server's own words
// rather than swallowed into "something went wrong".
//
// Two rules that hold across all of it:
//
//   · Money is integer CENTS. It is divided once, at the moment of display,
//     and never parsed back out of what is on screen.
//   · This is the only place in the API that hands out an attendee's EMAIL.
//     If it ever appears on a non-organiser surface, that is a leak.
// ═══════════════════════════════════════════════════════════════════

const TABS = ["updates", "feedback", "tiers", "promos", "attendees", "orders", "reschedule"] as const;
type Tab = (typeof TABS)[number];

/** Cents in, money out. One place, so no panel invents its own rounding. */
function money(cents: number, currency = "USD") {
  return `${currency === "USD" ? "$" : currency + " "}${(cents / 100).toFixed(2)}`;
}

export default function EventDeskLab() {
  const [eventId, setEventId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("updates");

  const { data, isLoading } = useQuery({
    queryKey: ["desk-events"],
    queryFn: () => events.listEvents({ limit: 30 }),
  });

  const list: EventSummary[] = data?.data.events ?? [];
  const current = list.find((e) => e.id === eventId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organiser desk"
        subtitle="Everything an organiser does to one event after it exists."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Events", href: "/lab/events" },
          { label: "Desk" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-1">Pick an event</h2>
        <p className="text-xs text-gray-500 mb-3">
          These panels are owner-only. On an event you do not run, expect a
          refusal — that is the guard working, and it is shown as the server
          words it.
        </p>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {list.map((e) => (
              <button
                key={e.id}
                onClick={() => setEventId(e.id)}
                className={`px-3 py-1.5 rounded-lg text-sm border text-left ${
                  eventId === e.id ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {e.title}
                <span className="text-gray-400 text-xs"> · {e.city}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {eventId && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="mb-4">
            <h2 className="font-semibold text-gray-900">{current?.title}</h2>
            <div className="flex gap-1 flex-wrap mt-3 border-b border-gray-200 pb-3">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                    tab === t ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {tab === "updates" && <Updates eventId={eventId} />}
          {tab === "feedback" && <Feedback eventId={eventId} />}
          {tab === "tiers" && <Tiers eventId={eventId} />}
          {tab === "promos" && <Promos eventId={eventId} />}
          {tab === "attendees" && (
            <>
              <Plates eventId={eventId} />
              <Attendees eventId={eventId} />
            </>
          )}
          {tab === "orders" && <Orders eventId={eventId} />}
          {tab === "reschedule" && <Reschedule eventId={eventId} />}
        </section>
      )}
    </div>
  );
}

// A shared runner: refusals here are content, so the server's sentence is what
// gets rendered rather than a generic failure line.
function useAction(keys: string[]) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      await fn();
      if (ok) setNote(ok);
      for (const k of keys) await qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const banner = (
    <>
      {note && <p className="text-sm text-green-700 mt-2">{note}</p>}
      {err && <p className="text-sm text-red-700 mt-2">{err}</p>}
    </>
  );

  return { run, busy, banner };
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

// ── Updates ────────────────────────────────────────────────────────

function Updates({ eventId }: { eventId: string }) {
  const [body, setBody] = useState("");
  const [type, setType] = useState<string>("general");
  const [pinned, setPinned] = useState(false);
  const { run, busy, banner } = useAction(["desk-updates"]);

  const { data, isLoading } = useQuery({
    queryKey: ["desk-updates", eventId],
    queryFn: () => events.listEventUpdates(eventId, { limit: 20 }),
  });
  const items = data?.data.items ?? [];

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        What the organiser tells everyone who is coming. The type is a promise
        about urgency — an <code>alert</code> is not a photo dump.
      </p>

      <div className="mb-4 space-y-2">
        <div className="flex gap-2 flex-wrap items-center">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          >
            {events.UPDATE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <label className="text-sm text-gray-600 flex items-center gap-1.5">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            pin it
          </label>
        </div>
        <TextAreaField
          label=""
          rows={2}
          placeholder="Parking is on the north side tonight…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button
          size="sm"
          isLoading={busy}
          disabled={!body.trim()}
          onClick={() =>
            run(async () => {
              await events.createEventUpdate(eventId, {
                body: body.trim(),
                updateType: type,
                isPinned: pinned,
              });
              setBody("");
            }, "Posted.")
          }
        >
          Post update
        </Button>
        {banner}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !items.length ? (
        <Empty>No updates yet.</Empty>
      ) : (
        <ul className="space-y-2">
          {items.map((u) => (
            <li key={u.id} className="border border-gray-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="blue">{u.updateType}</Badge>
                {u.isPinned && <Badge variant="purple">pinned</Badge>}
                {u.title && <span className="font-medium text-gray-900">{u.title}</span>}
                <span className="text-xs text-gray-400">
                  {new Date(u.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{u.body}</p>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Feedback ───────────────────────────────────────────────────────

function Feedback({ eventId }: { eventId: string }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const { run, busy, banner } = useAction(["desk-feedback"]);

  const { data, isLoading } = useQuery({
    queryKey: ["desk-feedback", eventId],
    queryFn: () => events.listEventFeedback(eventId, { limit: 20 }),
  });
  const items = data?.data.items ?? [];
  const summary = data?.data.summary;

  return (
    <>
      {summary && (
        <div className="flex gap-6 items-end mb-4">
          <div>
            {/* avgRating is NULLABLE — nobody has rated is not the same as 0. */}
            <div className="text-2xl font-semibold text-gray-900 tabular-nums">
              {summary.avgRating === null ? "—" : summary.avgRating.toFixed(1)}
            </div>
            <div className="text-xs text-gray-500">
              {summary.avgRating === null ? "nobody has rated it" : "average"}
            </div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-gray-900 tabular-nums">{summary.count}</div>
            <div className="text-xs text-gray-500">responses</div>
          </div>
          <div className="flex gap-1.5 pb-1 flex-wrap">
            {Object.entries(summary.distribution ?? {}).map(([star, n]) => (
              <Badge key={star} variant="gray">{star}★ {n}</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4 space-y-2 border-b border-gray-200 pb-4">
        <div className="flex gap-1.5 items-center">
          <span className="text-sm text-gray-600">Leave feedback:</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              className={`w-8 h-8 rounded-full text-sm ${
                rating === n ? "bg-gray-900 text-white" : "border border-gray-300 text-gray-600"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <TextAreaField
          label=""
          rows={2}
          placeholder="Optional comment…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <Button
          size="sm"
          isLoading={busy}
          onClick={() =>
            run(async () => {
              await events.createEventFeedback(eventId, {
                overallRating: rating,
                comment: comment.trim() || undefined,
              });
              setComment("");
            }, "Thanks — recorded.")
          }
        >
          Submit
        </Button>
        {banner}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !items.length ? (
        <Empty>No feedback yet.</Empty>
      ) : (
        <ul className="space-y-2">
          {items.map((f) => (
            <li key={f.id} className="border border-gray-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="green">{f.overallRating}★</Badge>
                {f.isAnonymous && <Badge variant="gray">anonymous</Badge>}
                {f.isFlagged && <Badge variant="yellow">flagged</Badge>}
                {f.wouldAttendAgain === true && <Badge variant="blue">would return</Badge>}
              </div>
              {f.comment && <p className="text-sm text-gray-700 mt-1">{f.comment}</p>}
              {f.organizerResponse && (
                <p className="text-sm text-gray-600 mt-1 border-l-2 border-gray-200 pl-3">
                  Organiser: {f.organizerResponse}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Tiers ──────────────────────────────────────────────────────────

function Tiers({ eventId }: { eventId: string }) {
  const { run, busy, banner } = useAction(["desk-tiers"]);
  const { data, isLoading } = useQuery({
    queryKey: ["desk-tiers", eventId],
    queryFn: () => events.listManagedTiers(eventId),
  });

  const tiers = data?.data.tiers ?? [];

  if (isLoading) return <Empty>Loading…</Empty>;
  if (!tiers.length) return <Empty>This event has no ticket tiers — it is not ticketed.</Empty>;

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        The organiser's own list: every kind, the ones taken off sale too, with
        how many sold. Guests read the public list, which says only what is
        left. <code>quantityAvailable: null</code> is unlimited — not zero,
        which means sold out. Delete removes only a kind nobody ever held.
      </p>
      <ul className="space-y-2">
        {tiers.map((t) => (
          <li key={t.id} className="border border-gray-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900">{t.name}</span>
              <Badge variant={t.priceCents === 0 ? "green" : "blue"}>
                {t.priceCents === 0 ? "free" : money(t.priceCents, t.currency)}
              </Badge>
              <Badge variant={t.saleState === "on_sale" ? "green" : "gray"}>
                {t.saleState.replace(/_/g, " ")}
              </Badge>
              {t.urgencyHint && t.urgencyHint !== "normal" && (
                <Badge variant="yellow">{t.urgencyHint.replace(/_/g, " ")}</Badge>
              )}
              {!t.isActive && <Badge variant="gray">inactive</Badge>}
              <span className="text-xs text-gray-500">
                {t.quantityTotal === -1
                  ? `${t.quantitySold} sold · unlimited`
                  : `${t.quantitySold} of ${t.quantityTotal} sold · ${t.quantityReserved} held`}
              </span>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                disabled={busy}
                onClick={() =>
                  run(
                    () => events.updateTier(eventId, t.id, { isActive: !t.isActive }),
                    t.isActive ? "Taken off sale." : "Back on sale.",
                  )
                }
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50"
              >
                {t.isActive ? "take off sale" : "put back on sale"}
              </button>
              <button
                disabled={busy}
                onClick={() => run(() => events.deleteTier(eventId, t.id), "Tier removed.")}
                className="text-xs px-2.5 py-1 rounded-lg text-red-700 disabled:opacity-50"
                title="Removes only a kind nobody ever held; otherwise take it off sale"
              >
                delete
              </button>
            </div>
          </li>
        ))}
      </ul>
      {banner}
    </>
  );
}

// ── Promo codes ────────────────────────────────────────────────────

function Promos({ eventId }: { eventId: string }) {
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(10);
  const [check, setCheck] = useState("");
  const [verdict, setVerdict] = useState<string | null>(null);
  const { run, busy, banner } = useAction(["desk-promos"]);

  const { data, isLoading } = useQuery({
    queryKey: ["desk-promos", eventId],
    queryFn: () => events.listPromoCodes(eventId),
  });
  const codes = data?.data.promoCodes ?? [];

  const { data: tierData } = useQuery({
    queryKey: ["desk-tiers", eventId],
    queryFn: () => events.listTiers(eventId),
  });
  const firstTier = (tierData?.data.tiers ?? [])[0];

  return (
    <>
      <div className="mb-4 flex gap-2 flex-wrap items-center">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="DIWALI25 (3+ chars)"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <input
          type="number"
          value={percent}
          min={1}
          max={100}
          onChange={(e) => setPercent(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-20"
        />
        <span className="text-sm text-gray-500">% off</span>
        <Button
          size="sm"
          isLoading={busy}
          disabled={code.trim().length < 3}
          onClick={() =>
            run(async () => {
              await events.createPromoCode(eventId, {
                code: code.trim(),
                type: "percent_off",
                percentOff: percent,
              });
              setCode("");
            }, "Code created.")
          }
        >
          Create
        </Button>
        {banner}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !codes.length ? (
        <Empty>No promo codes.</Empty>
      ) : (
        <ul className="space-y-2 mb-4">
          {codes.map((p) => (
            <li key={p.id} className="border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-2 flex-wrap">
              <code className="font-medium text-gray-900">{p.code}</code>
              <Badge variant="blue">
                {p.type === "percent_off" ? `${p.percentOff}% off` : money(p.amountOffCents ?? 0)}
              </Badge>
              {!p.isActive && <Badge variant="gray">inactive</Badge>}
              <span className="text-xs text-gray-500">
                used {p.redemptionsCount}
                {p.maxRedemptions ? ` of ${p.maxRedemptions}` : " (no cap)"}
              </span>
              <span className="ml-auto flex gap-2">
                <button
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => events.updatePromoCode(eventId, p.id, { isActive: !p.isActive }),
                      p.isActive ? "Deactivated." : "Reactivated.",
                    )
                  }
                  className="text-xs text-gray-600 disabled:opacity-50"
                >
                  {p.isActive ? "deactivate" : "reactivate"}
                </button>
                <button
                  disabled={busy}
                  onClick={() => run(() => events.deletePromoCode(eventId, p.id), "Deleted.")}
                  className="text-xs text-red-700 disabled:opacity-50"
                >
                  delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-1">Check a code the way checkout does</p>
        <p className="text-xs text-gray-500 mb-2">
          The answer carries a <strong>reason</strong> when it fails. That reason
          is the product — "expired" and "already used" need different words.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            value={check}
            onChange={(e) => setCheck(e.target.value.toUpperCase())}
            placeholder="Code to test"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={!check.trim() || !firstTier}
            onClick={async () => {
              setVerdict(null);
              try {
                const res = await events.validatePromoCode(eventId, check.trim(), [
                  { tierId: firstTier!.id, quantity: 1 },
                ]);
                const d = res.data;
                setVerdict(
                  d.valid
                    ? `valid — ${money(d.discountCents)} off`
                    : `not valid — ${d.reason ?? "no reason given"}`,
                );
              } catch (e) {
                setVerdict(e instanceof Error ? e.message : "check failed");
              }
            }}
          >
            Check
          </Button>
          {!firstTier && (
            <span className="text-xs text-gray-500">needs a tier to price against</span>
          )}
        </div>
        {verdict && <p className="text-sm text-gray-700 mt-2">{verdict}</p>}
      </div>
    </>
  );
}

// ── Plates per diet ────────────────────────────────────────────────

/** Counts only, never names: a diet fewer than three chose folds into "other". */
function Plates({ eventId }: { eventId: string }) {
  const { data, error } = useQuery({
    queryKey: ["desk-plates", eventId],
    queryFn: () => events.getEventPlates(eventId),
    retry: false,
  });
  if (error) return <p className="text-sm text-red-700 mb-3">Could not read the plates</p>;
  const p = data?.data;
  if (!p) return null;
  return (
    <div className="border border-gray-200 rounded-xl px-4 py-3 mb-4 text-sm">
      <div className="font-medium text-gray-900">{p.plates} plates</div>
      <ul className="text-gray-700 mt-1 space-y-0.5">
        {p.byDiet.map((d) => (
          <li key={d.diet}>· {d.label}: {d.count}</li>
        ))}
        {p.other > 0 && (
          <li>
            · Other: {p.other}
            {p.otherIncludes.length > 0 && (
              <span className="text-gray-500"> (includes {p.otherIncludes.map((d) => d.label).join(", ")})</span>
            )}
          </li>
        )}
      </ul>
      <p className="text-xs text-gray-500 mt-1">
        A diet fewer than {p.floor} people chose has no count of its own, so no guest can be picked out.
      </p>
    </div>
  );
}

// ── Attendees ──────────────────────────────────────────────────────

function Attendees({ eventId }: { eventId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["desk-attendees", eventId],
    queryFn: () => events.listOrganizerAttendees(eventId, { limit: 50 }),
    retry: false,
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  if (error)
    return (
      <p className="text-sm text-red-700">
        {error instanceof Error ? error.message : "Could not read the attendee list"}
      </p>
    );

  const rows = data?.data.attendees ?? [];
  if (!rows.length) return <Empty>Nobody holds a ticket yet.</Empty>;

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        The only surface in the API that returns an attendee's <strong>email</strong>.
        If you see it anywhere else, that is a leak.
      </p>
      <ListTotal shown={rows.length} meta={data?.meta} noun="ticket holders" />
      <ul className="space-y-2">
        {rows.map((a) => (
          <li key={a.ticketId} className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900">{a.attendeeName ?? a.user.name}</span>
              <span className="text-xs text-gray-500">{a.user.email}</span>
              <Badge variant="gray">{a.tier.name}</Badge>
              <Badge variant={a.checkedInAt ? "green" : "gray"}>
                {a.checkedInAt ? "checked in" : a.status}
              </Badge>
              <code className="text-xs text-gray-400 ml-auto">{a.ticketCode}</code>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/** The server's own count from `meta`, so a list longer than one page says so. */
function ListTotal({ shown, meta, noun }: { shown: number; meta?: events.OwnerListMeta; noun: string }) {
  if (!meta) return null;
  return (
    <p className="text-xs text-gray-500 mb-3 tabular-nums">
      Showing {shown} of {meta.total} {noun}
      {meta.totalPages > 1 ? ` · page ${meta.page} of ${meta.totalPages}` : ""}
    </p>
  );
}

// ── Orders + refunds ───────────────────────────────────────────────

function Orders({ eventId }: { eventId: string }) {
  const { run, busy, banner } = useAction(["desk-orders", "desk-attendees"]);
  const { data, isLoading, error } = useQuery({
    queryKey: ["desk-orders", eventId],
    queryFn: () => events.listOrganizerOrders(eventId, { limit: 50 }),
    retry: false,
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  if (error)
    return (
      <p className="text-sm text-red-700">
        {error instanceof Error ? error.message : "Could not read orders"}
      </p>
    );

  const rows = data?.data.orders ?? [];
  if (!rows.length) return <Empty>No orders.</Empty>;

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        Refunding with no ticket ids named refunds the <strong>whole order</strong>.
        The reply's <code>isFullRefund</code> says which actually happened.
      </p>
      <ListTotal shown={rows.length} meta={data?.meta} noun="orders" />
      <ul className="space-y-2">
        {rows.map((o) => (
          <li key={o.id} className="border border-gray-200 rounded-xl px-4 py-3 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900">{o.buyer.name}</span>
              <span className="text-xs text-gray-500">{o.buyer.email}</span>
              <Badge
                variant={
                  o.status === "paid" ? "green" : o.status.includes("refund") ? "yellow" : "gray"
                }
              >
                {o.status}
              </Badge>
              <span className="text-xs text-gray-500">
                {o.ticketCount} ticket{o.ticketCount === 1 ? "" : "s"}
              </span>
              <span className="ml-auto tabular-nums font-medium">
                {money(o.totalCents, o.currency)}
                {o.discountCents > 0 && (
                  <span className="text-xs text-gray-500 font-normal">
                    {" "}
                    (−{money(o.discountCents, o.currency)})
                  </span>
                )}
              </span>
            </div>
            {o.status === "paid" && (
              <button
                disabled={busy}
                onClick={() =>
                  run(
                    () => events.refundOrder(eventId, o.id, { reason: "Refunded from the Lab" }),
                    "Refund requested.",
                  )
                }
                className="text-xs text-red-700 mt-2 disabled:opacity-50"
              >
                refund the whole order
              </button>
            )}
          </li>
        ))}
      </ul>
      {banner}
    </>
  );
}

// ── Reschedule ─────────────────────────────────────────────────────

function Reschedule({ eventId }: { eventId: string }) {
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const { run, busy, banner } = useAction(["desk-events"]);

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        Moving an event answers with how many people were <strong>told</strong>.
        A reschedule nobody heard about is the failure that number exists to
        make visible — so it is shown rather than discarded.
      </p>
      <div className="flex gap-2 flex-wrap items-center mb-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why (optional)"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[12rem]"
        />
        <Button
          size="sm"
          isLoading={busy}
          disabled={!date}
          onClick={() =>
            run(async () => {
              const res = await events.rescheduleEvent(eventId, {
                eventDate: date,
                reason: reason.trim() || undefined,
              });
              setResult(
                `Moved to ${res.data.eventDate} — ${res.data.recipientsNotified} people notified.`,
              );
            })
          }
        >
          Move it
        </Button>
      </div>
      {result && <p className="text-sm text-green-700">{result}</p>}
      {banner}
    </>
  );
}
